import { AppState, DateRule, ExpenseRule, IncomeRule } from '../types'
import { StatementRow, importIds, merchantKey } from './csvImport'
import { candidateName } from './merchantNames'
import { daysInMonth, fromISODate, isWorkingDay } from './dateRules'
import { ReviewRow } from './statementImport'
import { makeId } from './id'
import { isExampleItem } from './storage'

/**
 * Spotting pay and bills in a statement, so a plan can start from one instead
 * of being typed in.
 *
 * Rows are grouped by merchant and direction. A group is regular when its
 * dates step evenly: the same day each month (give or take a weekend), every
 * four weeks, or every week. A month's statement shows a monthly bill only
 * once, so a single payment the bank marks as a Direct Debit, standing order
 * or credit transfer (BGC) counts too, flagged as seen once.
 *
 * Spending that varies and isn't a Direct Debit (the weekly shop) is left out:
 * that's a budget, not a bill.
 */

export interface RecurringSuggestion {
  /** kind|merchant, stable across renders. */
  key: string
  kind: 'income' | 'expense'
  merchant: string
  name: string
  /** Always positive, from the latest occurrence. */
  amount: number
  frequency: 'monthly' | 'weekly' | 'four_weekly'
  dateRule: DateRule
  dayOfWeek?: number
  /** For weekly and four-weekly: a real occurrence, which fixes the phase. */
  startDate?: string
  dates: string[]
  seenOnce: boolean
  /** Amounts differ by more than 10%: an estimate, recorded as actuals later. */
  variable: boolean
}

const MANDATE = /\b(DD|D\/D|DIRECT ?DEBIT|SO|S\/O|STANDING ORDER|BGC|BANK GIRO)\b/
const MIN_AMOUNT = 1
const VARIABLE_SHARE = 0.1
const DAY_MS = 86400000

const dayGap = (a: string, b: string) => Math.round((fromISODate(b).getTime() - fromISODate(a).getTime()) / DAY_MS)

export function detectRecurring(state: AppState, rows: ReviewRow[]): RecurringSuggestion[] {
  // The starter plan's examples are placeholders, not what the person pays:
  // real rent of exactly £1,200 is still rent to find.
  const incomes = state.incomes.filter((i) => !isExampleItem(i))
  const expenses = state.expenses.filter((i) => !isExampleItem(i))
  const known = new Set<string>()
  for (const item of [...state.budgets, ...expenses]) for (const p of item.matchPatterns ?? []) known.add(p)
  for (const item of [...incomes, ...expenses]) known.add(merchantKey(item.name))
  // History before the plan starts can't be matched to it by date, so pay or
  // a bill of exactly an amount the plan already has is taken to be that.
  const amounts = new Set([...incomes, ...expenses].filter((i) => i.active).map((i) => `${i.kind}|${i.amount.toFixed(2)}`))

  const groups = new Map<string, ReviewRow[]>()
  for (const row of rows) {
    if (Math.abs(row.amount) < MIN_AMOUNT || row.merchant.length < 2) continue
    // The bank's payee where it has one: a reference that changes each month
    // ("RENT AUG", "RENT SEP") would otherwise make every month a new merchant.
    const who = (row.payee && merchantKey(row.payee)) || row.merchant
    const key = `${row.amount > 0 ? 'income' : 'expense'}|${who}`
    groups.set(key, [...(groups.get(key) ?? []), row])
  }

  const out: RecurringSuggestion[] = []
  for (const [key, group] of groups) {
    if (known.has(key.slice(key.indexOf('|') + 1)) || group.some((r) => known.has(r.merchant))) continue
    if (group.some((r) => r.suggested.kind === 'covered' || r.suggested.kind === 'actual')) continue
    const suggestion = fromGroup(key, group.sort((a, b) => a.date.localeCompare(b.date)))
    if (suggestion && !amounts.has(`${suggestion.kind}|${suggestion.amount.toFixed(2)}`)) out.push(suggestion)
  }
  // Pay first, then the biggest outgoings.
  return out.sort((a, b) => (a.kind === b.kind ? b.amount - a.amount : a.kind === 'income' ? -1 : 1))
}

function fromGroup(key: string, group: ReviewRow[]): RecurringSuggestion | null {
  const kind = group[0].amount > 0 ? 'income' : 'expense'
  const latest = group[group.length - 1]
  const amounts = group.map((r) => Math.abs(r.amount))
  const variable = Math.max(...amounts) - Math.min(...amounts) > Math.max(...amounts) * VARIABLE_SHARE
  const mandate = group.some((r) => MANDATE.test(`${r.type ?? ''} ${r.description}`.toUpperCase()))
  if (variable && kind === 'expense' && !mandate) return null

  const dates = group.map((r) => r.date)
  const base = {
    key,
    kind,
    merchant: latest.merchant,
    name: candidateName(latest) ?? latest.description,
    amount: Math.abs(latest.amount),
    dates,
    variable,
  } as const

  if (group.length === 1) {
    if (!mandate) return null
    return { ...base, frequency: 'monthly', dateRule: monthlyRule(kind, dates), seenOnce: true }
  }
  const gaps = dates.slice(1).map((d, i) => dayGap(dates[i], d))
  const weekday = fromISODate(latest.date).getDay()
  const shared = { ...base, dateRule: { type: 'exact', dayOfMonth: 1 } as DateRule, dayOfWeek: weekday, startDate: latest.date, seenOnce: false }
  // Two dates 28 days apart could be either; three settle it, as four-weekly drifts through the month.
  const fourWeekly = gaps.every((g) => g >= 27 && g <= 29)
  if (fourWeekly && dates.length > 2) return { ...shared, frequency: 'four_weekly' }
  if (isMonthly(dates)) return { ...base, frequency: 'monthly', dateRule: monthlyRule(kind, dates), seenOnce: false }
  if (fourWeekly) return { ...shared, frequency: 'four_weekly' }
  if (gaps.every((g) => g >= 6 && g <= 8)) return { ...shared, frequency: 'weekly' }
  return null
}

/** Once a calendar month, all within three days of the same date (or all at the month's end). */
function isMonthly(dates: string[]): boolean {
  const days = dates.map(fromISODate)
  const stepByMonth = days.slice(1).every((b, i) => {
    const a = days[i]
    return (b.getFullYear() - a.getFullYear()) * 12 + b.getMonth() - a.getMonth() === 1
  })
  const doms = days.map((d) => d.getDate())
  return stepByMonth && (Math.max(...doms) - Math.min(...doms) <= 3 || days.every(nearMonthEnd))
}

/** In the last four days: the last working day can be the Friday before a bank holiday Monday. */
function nearMonthEnd(d: Date): boolean {
  return daysInMonth(d.getFullYear(), d.getMonth()) - d.getDate() < 4
}

/**
 * Which day, and which way it moves off weekends. Pay comes the working day
 * before; Direct Debits go the working day after. A payment seen on a weekend
 * evidently doesn't move (a card subscription), so it's the exact date.
 */
function monthlyRule(kind: 'income' | 'expense', dates: string[]): DateRule {
  const days = dates.map(fromISODate)
  const onWeekend = days.some((d) => !isWorkingDay(d))
  if (days.every(nearMonthEnd) && dates.length > 1) {
    return { type: kind === 'income' && !onWeekend ? 'working_day_before' : 'exact', dayOfMonth: 'last' }
  }
  const doms = days.map((d) => d.getDate())
  if (onWeekend) return { type: 'exact', dayOfMonth: mostCommon(doms) }
  return kind === 'income'
    ? { type: 'working_day_before', dayOfMonth: Math.max(...doms) }
    : { type: 'working_day_after', dayOfMonth: Math.min(...doms) }
}

function mostCommon(ns: number[]): number {
  const counts = new Map<number, number>()
  for (const n of ns) counts.set(n, (counts.get(n) ?? 0) + 1)
  return [...counts].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0][0]
}

/** The plan item a ticked suggestion becomes. */
export function toRule(s: RecurringSuggestion): IncomeRule | ExpenseRule {
  const common = {
    id: makeId(),
    name: s.name,
    amount: s.amount,
    frequency: s.frequency,
    dateRule: s.dateRule,
    ...(s.dayOfWeek !== undefined ? { dayOfWeek: s.dayOfWeek, startDate: s.startDate } : {}),
    active: true,
  }
  // Outgoings remember the merchant, so the next import records what a
  // variable bill really came to. Pay has no match patterns.
  return s.kind === 'income'
    ? { ...common, kind: 'income' }
    : { ...common, kind: 'expense', matchPatterns: [s.merchant] }
}

/**
 * Rows from several statements as one, oldest first. Where two statements
 * overlap, a row both carry is kept once: ids are worked out per statement,
 * where they mean "the nth identical row that day".
 */
export function mergeStatements(statements: StatementRow[][]): StatementRow[] {
  const seen = new Set<string>()
  const out: StatementRow[] = []
  for (const rows of statements) {
    importIds(rows).forEach((id, i) => {
      if (seen.has(id)) return
      seen.add(id)
      out.push(rows[i])
    })
  }
  return out.sort((a, b) => a.date.localeCompare(b.date))
}
